/**
 * SegmentedStepIndicator — Replaces badge-based step indicators.
 *
 * Horizontal bar divided into N segments:
 * - Completed: filled with accent gradient
 * - Current: accent colour with pulse
 * - Upcoming: disabled fill
 *
 * @see documents/ui-enhancement-spec.md §10.2
 */

import React from 'react';
import { BlockStack, InlineStack, Text } from '@shopify/polaris';
import { useZeliumTheme } from '../../theme';

interface SegmentedStepIndicatorProps {
  /** Total number of steps */
  totalSteps: number;
  /** Current step (0-indexed) */
  currentStep: number;
  /** Optional labels for each step */
  labels?: string[];
}

export function SegmentedStepIndicator({
  totalSteps,
  currentStep,
  labels,
}: SegmentedStepIndicatorProps): React.ReactElement {
  const { motion } = useZeliumTheme();

  return (
    <BlockStack gap="200">
      <div
        style={{
          display: 'flex',
          gap: '4px',
          width: '100%',
        }}
      >
        {Array.from({ length: totalSteps }, (_, i) => {
          const isCompleted = i < currentStep;
          const isCurrent = i === currentStep;

          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: '6px',
                borderRadius: 'var(--z-radius-pill)',
                background: isCompleted
                  ? 'var(--z-gradient-accent)'
                  : isCurrent
                    ? 'var(--z-accent)'
                    : 'var(--p-color-bg-fill-disabled)',
                transition: `background ${motion.duration.normal}s ${motion.ease.default}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {isCurrent && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    border: '1.5px solid var(--z-accent)',
                    animation: 'zelium-pulse 2s ease-in-out infinite',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {labels && labels.length > 0 && (
        <InlineStack align="space-between">
          {labels.map((label, i) => (
            <Text
              key={i}
              as="span"
              variant="bodySm"
              fontWeight={i === currentStep ? 'semibold' : 'regular'}
              tone={i <= currentStep ? undefined : 'subdued'}
            >
              {label}
            </Text>
          ))}
        </InlineStack>
      )}

      {/* Inline keyframes for the pulse animation */}
      <style>{`
        @keyframes zelium-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </BlockStack>
  );
}
